import { Smile } from "@styled-icons/boxicons-regular";
import { observer } from "mobx-react-lite";
import { Channel, Message, Server, User } from "revolt.js";
import styled from "styled-components/macro";
import { Text } from "preact-i18n";
import { useCallback, useEffect, useMemo, useState } from "preact/hooks";

import {
    Avatar,
    Checkbox,
    getCurrentModalBase,
    Modal,
    TextField,
    UserStatus,
} from "@revoltchat/ui";

import { getRolesInChannel } from "../../../lib/getRolesInChannel";
import { useTranslation } from "../../../lib/i18n";
import { displayMentions } from "../../../lib/mentions";

import { useApplicationState } from "../../../mobx/State";

import groupFallback from "../../../components/common/assets/group.png";

import ChannelIcon from "../../../components/common/ChannelIcon";
import { EmojiPicker } from "../../../components/common/messaging/MessageBox";
import ServerIcon from "../../../components/common/ServerIcon";
import { useClient } from "../../client/ClientController";
import { takeError } from "../../client/jsx/error";
import { modalController } from "../ModalController";
import { ModalProps } from "../types";

interface CategoryEntry {
    name: string;
    channels: Array<{ channel: Channel; server: Server; category: string }>;
}

interface ServerEntry {
    server: Server;
    categories: CategoryEntry[];
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-height: 0;
    padding-top: 0.25rem;
`;

const TitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const CloseBtn = styled.button<{ type?: string }>`
    width: 32px;
    height: 32px;
    border: none;
    border-radius: var(--border-radius);
    background: none;
    color: var(--secondary-foreground);
    cursor: pointer;
    font-size: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
        background: var(--secondary-background);
        color: var(--foreground);
    }
`;

const ListSection = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
`;

const PreviewBox = styled.div`
    flex-shrink: 0;

    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    color: var(--secondary-foreground);
    background: var(--secondary-background);
    border-radius: var(--border-radius);
    border-left: 3px solid var(--accent);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const SectionHeader = styled.div`
    padding: 0.5rem 0.75rem 0.25rem;
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--secondary-foreground);
    letter-spacing: 0.05em;
`;

const EmojiBtn = styled.button<{ type?: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: var(--border-radius);
    background: none;
    color: var(--secondary-foreground);
    cursor: pointer;

    &:hover {
        background: var(--secondary-background);
        color: var(--foreground);
    }
`;

const SendButton = styled.button<{ $disabled: boolean }>`
    display: block;
    width: 100%;
    margin-top: 0.5rem;
    padding: 0.5rem;
    border: none;
    border-radius: var(--border-radius);
    background: ${({ $disabled }) =>
        $disabled ? "var(--secondary-background)" : "var(--accent)"};
    color: ${({ $disabled }) =>
        $disabled ? "var(--secondary-foreground)" : "var(--accent-contrast)"};
    font-weight: 600;
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
`;

const TruncatedText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DmTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const Chevron = styled.span<{ $open: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    transition: transform 0.15s;
    transform: rotate(${({ $open }) => ($open ? "90deg" : "0deg")});
    color: var(--secondary-foreground);
    font-size: 0.75rem;
    flex-shrink: 0;
`;

const ServerNode = styled.div``;

const TreeNodeHeader = styled.button<{ $level?: number; type?: string }>`
    display: flex;
    align-items: center;
    gap: 0.35rem;
    width: 100%;
    padding: 0.35rem 0.75rem;
    border: none;
    border-radius: var(--border-radius);
    background: none;
    cursor: pointer;
    color: var(--foreground);
    font-size: 0.8125rem;
    text-align: left;
    font-weight: ${({ $level }) => ($level === 0 ? 700 : 500)};
    color: ${({ $level }) =>
        $level === 0 ? "var(--foreground)" : "var(--secondary-foreground)"};

    &:hover {
        background: var(--secondary-background);
    }
`;

const CategoryNode = styled.div`
    padding-left: 1.5rem;
`;

const ChannelItem = styled.div`
    padding-left: 1.5rem;
`;

const InputWrapper = styled.div`
    display: flex;
    gap: 0.25rem;
    align-items: center;
`;

function pickPluralKey(n: number, locale: string, base: string): string {
    if (locale === "ru") {
        const mod100 = n % 100;
        const mod10 = n % 10;
        if (mod100 >= 11 && mod100 <= 19) return `${base}_5`;
        if (mod10 === 1) return `${base}_1`;
        if (mod10 >= 2 && mod10 <= 4) return `${base}_2`;
        return `${base}_5`;
    }
    return n === 1 ? `${base}_1` : `${base}_2`;
}

const id = "app.special.popovers.forward_picker";

export default observer(
    ({ messageId: propMessageId, onClose }: ModalProps<"forward_picker">) => {
        const client = useClient();
        const appState = useApplicationState();
        const t = useTranslation();
        const [filter, setFilter] = useState("");
        const [selected, setSelected] = useState(new Set<string>());
        const [msgText, setMsgText] = useState("");
        const [loading, setLoading] = useState(false);
        const [showEmoji, setShowEmoji] = useState(false);
        const [expandedServers, setExpandedServers] = useState<Set<string>>(
            new Set(),
        );
        const [expandedCategories, setExpandedCategories] = useState<
            Set<string>
        >(new Set());

        useEffect(() => {
            function onEscape(e: KeyboardEvent) {
                if (e.key === "Escape") {
                    onClose();
                }
            }
            const base = getCurrentModalBase();
            function onBaseClick(e: MouseEvent) {
                if (e.target === base) onClose();
            }
            document.addEventListener("keydown", onEscape);
            base?.addEventListener("click", onBaseClick);
            return () => {
                document.removeEventListener("keydown", onEscape);
                base?.removeEventListener("click", onBaseClick);
            };
        }, [onClose]);

        const messages = useMemo(() => {
            const ids = propMessageId
                ? [propMessageId]
                : appState.layout.getSelectedMessages();
            return ids.map((id) => client.messages.get(id)).filter(Boolean);
        }, [client.messages, propMessageId]);

        // Collect DMs; build server→category→channel tree from client.servers
        const { dms, serverTree, savedMessages } = useMemo(() => {
            const dms: Array<{ channel: Channel; user: User }> = [];
            const byServer = new Map<
                string,
                { server: Server; byCat: Map<string, Channel[]> }
            >();

            for (const server of client.servers.values()) {
                const byCat = new Map<string, Channel[]>();

                for (const cat of server.orderedChannels) {
                    for (const ch of cat.channels) {
                        if (ch.channel_type === "Voice") continue;
                        if (!ch.havePermission("SendMessage")) continue;
                        const arr = byCat.get(cat.title);
                        if (arr) {
                            arr.push(ch);
                        } else {
                            byCat.set(cat.title, [ch]);
                        }
                    }
                }

                if (byCat.size > 0) {
                    byServer.set(server._id, { server, byCat });
                }
            }

            // DMs / Groups — iterate channels (not in server tree)
            let savedMessages: { channel: Channel; user: User } | undefined;
            for (const ch of client.channels.values()) {
                if (!ch.havePermission("SendMessage")) continue;

                if (ch.channel_type === "DirectMessage" && ch.active) {
                    const userId = ch.recipient_ids?.find(
                        (x: string) => x !== client.user?._id,
                    );
                    if (userId) {
                        const user = client.users.get(userId);
                        dms.push({
                            channel: ch,
                            user:
                                user ||
                                ({
                                    _id: userId,
                                    username: userId,
                                    display_name: null,
                                    status: undefined,
                                    generateAvatarURL: () => null,
                                } as unknown as User),
                        });
                    }
                } else if (ch.channel_type === "Group") {
                    const groupName = ch.name || "Group";
                    const fakeUser = {
                        display_name: groupName,
                        username: groupName.toLowerCase(),
                        status: undefined,
                        _id: ch._id,
                        generateAvatarURL: (args: any) =>
                            ch.generateIconURL(args) || groupFallback,
                    } as unknown as User;
                    dms.push({ channel: ch, user: fakeUser });
                } else if (ch.channel_type === "SavedMessages") {
                    savedMessages = {
                        channel: ch,
                        user: {
                            display_name: t("app.navigation.tabs.saved"),
                            username: "saved_messages",
                            status: undefined,
                            _id: ch._id,
                            generateAvatarURL: () => null,
                        } as unknown as User,
                    };
                }
            }

            dms.sort((b, a) =>
                a.channel.last_message_id_or_past.localeCompare(
                    b.channel.last_message_id_or_past,
                ),
            );

            return { dms, serverTree: byServer, savedMessages };
        }, [client.channels, client.servers]);

        const filterLower = filter.toLowerCase();

        const visibleDms = useMemo(
            () =>
                dms.filter(
                    (d) =>
                        selected.has(d.channel._id) ||
                        d.user.display_name
                            ?.toLowerCase()
                            .includes(filterLower) ||
                        d.user.username?.toLowerCase().includes(filterLower),
                ),
            [dms, filterLower, selected],
        );

        const visibleServers = useMemo(() => {
            const result = new Map<string, ServerEntry>();

            for (const [serverId, { server, byCat }] of serverTree) {
                const categories: CategoryEntry[] = [];

                for (const [catTitle, chs] of byCat) {
                    const visibleChs = chs.filter(
                        (ch) =>
                            selected.has(ch._id) ||
                            ch.name?.toLowerCase().includes(filterLower) ||
                            catTitle.toLowerCase().includes(filterLower) ||
                            server.name?.toLowerCase().includes(filterLower),
                    );
                    if (visibleChs.length === 0) continue;
                    categories.push({
                        name: catTitle,
                        channels: visibleChs.map((ch) => ({
                            channel: ch,
                            server,
                            category: catTitle,
                        })),
                    });
                }

                if (categories.length > 0) {
                    result.set(serverId, { server, categories });
                }
            }

            return result;
        }, [serverTree, filterLower, selected]);

        const toggle = useCallback((id: string) => {
            setSelected((prev) => {
                const next = new Set(prev);
                next.has(id) ? next.delete(id) : next.add(id);
                return next;
            });
        }, []);

        const toggleServer = useCallback((id: string) => {
            setExpandedServers((prev) => {
                const next = new Set(prev);
                next.has(id) ? next.delete(id) : next.add(id);
                return next;
            });
        }, []);

        const toggleCategory = useCallback((key: string) => {
            setExpandedCategories((prev) => {
                const next = new Set(prev);
                next.has(key) ? next.delete(key) : next.add(key);
                return next;
            });
        }, []);

        const messageCount = messages.filter(Boolean).length;
        const attachmentCount = messages.reduce(
            (s, m) => s + (m?.attachments?.length ?? 0),
            0,
        );

        const locale = appState.locale.getLanguage();

        const previewInfo = useMemo(():
            | { type: "raw"; text: string }
            | { type: "i18n"; key: string; n: number }
            | { type: "empty" } => {
            if (messageCount === 0) return { type: "empty" };
            if (messageCount === 1) {
                const m = messages[0]!;
                if (m.content) {
                    const text = displayMentions(
                        m.content,
                        client,
                        m.channel?.server_id,
                        m.channel ? getRolesInChannel(m.channel) : [],
                    );
                    return {
                        type: "raw",
                        text: text.length > 60 ? `${text.slice(0, 60)}…` : text,
                    };
                }
                const n = m.attachments?.length ?? 0;
                if (n > 0)
                    return {
                        type: "i18n",
                        key: pickPluralKey(n, locale, `${id}.n_attachments`),
                        n,
                    };
                return { type: "empty" };
            }
            const hasText = messages.some((m) => m?.content);
            if (hasText)
                return {
                    type: "i18n",
                    key: pickPluralKey(
                        messageCount,
                        locale,
                        `${id}.n_messages`,
                    ),
                    n: messageCount,
                };
            if (attachmentCount > 0)
                return {
                    type: "i18n",
                    key: pickPluralKey(
                        attachmentCount,
                        locale,
                        `${id}.n_attachments`,
                    ),
                    n: attachmentCount,
                };
            return {
                type: "i18n",
                key: pickPluralKey(messageCount, locale, `${id}.n_messages`),
                n: messageCount,
            };
        }, [messages, messageCount, attachmentCount, locale, client]);

        const handleSend = useCallback(async () => {
            if (selected.size === 0) return;
            const ids = propMessageId
                ? [propMessageId]
                : appState.layout.getSelectedMessages();
            setLoading(true);
            let firstError: string | null = null;
            for (const targetId of selected) {
                try {
                    const target = client.channels.get(targetId);
                    if (!target) continue;
                    if (msgText.trim()) {
                        await target.sendMessage(msgText.trim());
                    }
                    const sortedMessages = ids
                        .map((id) => ({ id, msg: client.messages.get(id) }))
                        .filter((x) => x.msg);
                    sortedMessages.sort(
                        (a, b) => a.msg!.createdAt - b.msg!.createdAt,
                    );

                    const resolveOriginalId = (msg: Message): string => {
                        let current = msg;
                        while (current && current.forward_id) {
                            const parent = client.messages.get(
                                current.forward_id,
                            );
                            if (!parent) break;
                            current = parent;
                        }
                        return current._id;
                    };

                    await client.api.post(
                        `/channels/${target._id}/messages/forward`,
                        {
                            forwards: sortedMessages.map((x) => ({
                                id: resolveOriginalId(x.msg!),
                            })),
                        },
                    );
                } catch (err) {
                    if (!firstError) firstError = takeError(err);
                }
            }
            if (!firstError) {
                appState.layout.exitSelection();
                onClose();
            } else {
                modalController.push({ type: "error", error: firstError });
            }
            setLoading(false);
        }, [
            selected,
            msgText,
            propMessageId,
            client.channels,
            client.api,
            onClose,
        ]);

        return (
            <Modal
                title={
                    <TitleRow>
                        <Text id={`${id}.title`} />
                        <CloseBtn onClick={onClose}>✕</CloseBtn>
                    </TitleRow>
                }
                description={<Text id={`${id}.subtitle`} />}
                actions={[]}>
                <Container>
                    <TextField
                        value={filter}
                        onChange={(v) => setFilter(v)}
                        placeholder={t(`${id}.search`)}
                    />

                    <ListSection>
                        {savedMessages &&
                            (selected.has(savedMessages.channel._id) ||
                                savedMessages.user.display_name
                                    ?.toLowerCase()
                                    .includes(filterLower) ||
                                savedMessages.user.username
                                    ?.toLowerCase()
                                    .includes(filterLower)) && (
                                <>
                                    <SectionHeader>
                                        <Text id={`${id}.saved_messages`} />
                                    </SectionHeader>
                                    <Checkbox
                                        key={savedMessages.channel._id}
                                        value={selected.has(
                                            savedMessages.channel._id,
                                        )}
                                        onChange={() =>
                                            toggle(savedMessages.channel._id)
                                        }
                                        title={
                                            <DmTitle>
                                                <Avatar
                                                    size={28}
                                                    src={savedMessages.user.generateAvatarURL(
                                                        { max_side: 128 },
                                                    )}
                                                    fallback={
                                                        savedMessages.user
                                                            .username?.[0]
                                                    }
                                                />
                                                <TruncatedText>
                                                    {
                                                        savedMessages.user
                                                            .display_name
                                                    }
                                                </TruncatedText>
                                            </DmTitle>
                                        }
                                    />
                                </>
                            )}
                        {visibleServers.size > 0 && (
                            <>
                                <SectionHeader>
                                    <Text id={`${id}.servers`} />
                                </SectionHeader>
                                {Array.from(visibleServers.values()).map(
                                    ({ server, categories }) => {
                                        const serverOpen = expandedServers.has(
                                            server._id,
                                        );
                                        return (
                                            <ServerNode key={server._id}>
                                                <TreeNodeHeader
                                                    $level={0}
                                                    onClick={() =>
                                                        toggleServer(server._id)
                                                    }>
                                                    <Chevron $open={serverOpen}>
                                                        ▶
                                                    </Chevron>
                                                    <ServerIcon
                                                        target={server}
                                                        size={24}
                                                    />
                                                    <TruncatedText>
                                                        {server.name}
                                                    </TruncatedText>
                                                </TreeNodeHeader>
                                                {serverOpen && (
                                                    <>
                                                        {categories.map(
                                                            ({
                                                                name: catName,
                                                                channels: chs,
                                                            }) => {
                                                                const catKey = `${server._id}/${catName}`;
                                                                const catOpen =
                                                                    expandedCategories.has(
                                                                        catKey,
                                                                    );
                                                                return (
                                                                    <CategoryNode
                                                                        key={
                                                                            catKey
                                                                        }>
                                                                        <TreeNodeHeader
                                                                            $level={
                                                                                1
                                                                            }
                                                                            onClick={() =>
                                                                                toggleCategory(
                                                                                    catKey,
                                                                                )
                                                                            }>
                                                                            <Chevron
                                                                                $open={
                                                                                    catOpen
                                                                                }>
                                                                                ▶
                                                                            </Chevron>
                                                                            <TruncatedText>
                                                                                {
                                                                                    catName
                                                                                }
                                                                            </TruncatedText>
                                                                        </TreeNodeHeader>
                                                                        {catOpen && (
                                                                            <>
                                                                                {chs.map(
                                                                                    ({
                                                                                        channel,
                                                                                    }) => (
                                                                                        <ChannelItem
                                                                                            key={
                                                                                                channel._id
                                                                                            }>
                                                                                            <Checkbox
                                                                                                value={selected.has(
                                                                                                    channel._id,
                                                                                                )}
                                                                                                onChange={() =>
                                                                                                    toggle(
                                                                                                        channel._id,
                                                                                                    )
                                                                                                }
                                                                                                title={
                                                                                                    <DmTitle>
                                                                                                        <ChannelIcon
                                                                                                            target={
                                                                                                                channel
                                                                                                            }
                                                                                                            size={
                                                                                                                16
                                                                                                            }
                                                                                                        />
                                                                                                        <TruncatedText>
                                                                                                            {
                                                                                                                channel.name
                                                                                                            }
                                                                                                        </TruncatedText>
                                                                                                    </DmTitle>
                                                                                                }
                                                                                            />
                                                                                        </ChannelItem>
                                                                                    ),
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </CategoryNode>
                                                                );
                                                            },
                                                        )}
                                                    </>
                                                )}
                                            </ServerNode>
                                        );
                                    },
                                )}
                            </>
                        )}

                        {visibleDms.length > 0 && (
                            <>
                                <SectionHeader>
                                    <Text id={`${id}.direct_messages`} />
                                </SectionHeader>
                                {visibleDms.map(({ channel, user }) => (
                                    <Checkbox
                                        key={channel._id}
                                        value={selected.has(channel._id)}
                                        onChange={() => toggle(channel._id)}
                                        title={
                                            <DmTitle>
                                                <Avatar
                                                    size={28}
                                                    src={user.generateAvatarURL(
                                                        { max_side: 128 },
                                                    )}
                                                    fallback={
                                                        user.username?.[0]
                                                    }
                                                    overlay={
                                                        channel.channel_type ===
                                                        "DirectMessage" ? (
                                                            <UserStatus
                                                                user={user}
                                                            />
                                                        ) : null
                                                    }
                                                />
                                                <TruncatedText>
                                                    {user.display_name ||
                                                        user.username}
                                                </TruncatedText>
                                            </DmTitle>
                                        }
                                    />
                                ))}
                            </>
                        )}
                    </ListSection>

                    <PreviewBox>
                        {previewInfo.type !== "empty" &&
                            (previewInfo.type === "raw" ? (
                                <span>{previewInfo.text}</span>
                            ) : (
                                <Text
                                    id={previewInfo.key}
                                    fields={{ n: previewInfo.n }}
                                />
                            ))}
                    </PreviewBox>

                    <InputWrapper>
                        <TextField
                            value={msgText}
                            onChange={(v) => setMsgText(v)}
                            placeholder={t(`${id}.add_message`)}
                        />
                        <EmojiBtn onClick={() => setShowEmoji((v) => !v)}>
                            <Smile size={20} />
                        </EmojiBtn>
                    </InputWrapper>
                    {showEmoji && (
                        <EmojiPicker
                            onSelect={(emoji: string) =>
                                setMsgText((p) => p + emoji)
                            }
                            onClose={() => setShowEmoji(false)}
                        />
                    )}
                    <SendButton
                        $disabled={selected.size === 0 || loading}
                        disabled={selected.size === 0 || loading}
                        onClick={handleSend}>
                        <Text id={`${id}.send`} />
                    </SendButton>
                </Container>
            </Modal>
        );
    },
);
